import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { fetchUserData, logout } from "../../redux/authSlice";
import { AppDispatch, RootState } from "../../redux/store";
import {
  listenToUserWallet,
  listenToUserSavedCards,
  listenToUserTransactions,
  cleanupWalletListeners,
  Transaction,
  SavedCard,
} from "../../redux/transactionsSlice";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";
import logo from "../../assets/cosmoola/logo.jpg";
import watchImg from "../../assets/cosmoola/watch.jpg";
import {
  Bell, Camera, Shield, ShieldCheck, Package, RefreshCw, RotateCcw,
  CreditCard, Headphones, Pencil, Store, Home as HomeIcon, BarChart2,
  Settings, LogOut, Plus, Copy, Users, ChevronRight, Wallet, LayoutDashboard,
  ClipboardList, UserCheck, MapPin, Star, TrendingUp, ArrowUpRight,
  ArrowDownLeft, Zap, Globe, Lock, Eye, EyeOff, CheckCircle, X, Menu,
  Search, User, ShoppingBag, ExternalLink, PlusCircle, AlertCircle, Loader2,
} from "lucide-react";

/* ─── constants ──────────────────────────────────────────── */
const EUR = "\u20ac";

const D = { bg:"#000000", surface:"#0a0a0a", surface2:"#101010", border:"#1c1c1c", border2:"#2a2a2a", white:"#ffffff", offwhite:"#e8e8e8", muted:"#6b6b6b", subtle:"#3a3a3a", gold:"#C9A96E" };
const L = { bg:"#ffffff", surface:"#fafafa", surface2:"#f3f3f3", border:"#e5e5e5", border2:"#d4d4d4", text:"#0a0a0a", textSoft:"#525252", muted:"#a3a3a3", subtle:"#d4d4d4" };

/* ─── Card brand colours (matches PaymentMethods) ── */
const BRAND_COLORS: Record<string, { bg: string; label: string }> = {
  visa:       { bg: "#1a1f71", label: "VISA" },
  mastercard: { bg: "#252525", label: "MC"   },
  amex:       { bg: "#007bc1", label: "AMEX" },
  discover:   { bg: "#ff6600", label: "DISC" },
  default:    { bg: "#1a1a1a", label: "CARD" },
};

const maskName  = (n: string) => !n ? "" : n.length <= 2 ? n : n[0] + "\u00b7".repeat(n.length - 1);
const maskEmail = (e: string) => { if (!e) return ""; const [u, d] = e.split("@"); return !d ? e : u.slice(0,3) + "\u00b7\u00b7\u00b7@" + d; };
const fmt       = (n: number, sym = EUR) => sym + Number(n || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 });

/* ─── Format a Firestore/ISO date to a human-readable label ── */
const fmtDate = (raw: string | null): string => {
  if (!raw) return "";
  const d   = new Date(raw);
  const now = new Date();
  const diffMs   = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return `Today, ${d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`;
  if (diffDays === 1) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

/* ─── Transaction label ── */
const txnLabel = (t: Transaction): string => {
  if (t.type === "topup") return `Top-up via ${t.cardBrand ? t.cardBrand.charAt(0).toUpperCase() + t.cardBrand.slice(1) : "Card"} ••••${t.cardLast4 ?? ""}`;
  if (t.type === "transfer_out") return `Transfer to ${(t as any).recipientName ?? "Account"}`;
  if (t.type === "transfer_in")  return `Received from ${(t as any).senderName ?? "Account"}`;
  if (t.type === "fee")          return "COSMOOLA Fee";
  if (t.type === "withdrawal")   return "Withdrawal";
  return t.type ?? "Transaction";
};

const txnDirection = (t: Transaction): "in" | "out" =>
  ["topup", "transfer_in", "refund"].includes(t.type) ? "in" : "out";

const txnAmount = (t: Transaction): number =>
  // Use walletCredit for top-ups (what actually landed in wallet), else amount
  t.type === "topup" ? (t.walletCredit ?? t.grossAmount ?? t.amount ?? 0) : (t.amount ?? 0);

/* ─── Monthly stats derived from transactions ── */
const useMonthlyStats = (transactions: Transaction[]) => {
  return useMemo(() => {
    const now       = new Date();
    const thisMonth = now.getMonth();
    const thisYear  = now.getFullYear();
    const lastMonth = thisMonth === 0 ? 11 : thisMonth - 1;
    const lastYear  = thisMonth === 0 ? thisYear - 1 : thisYear;

    let inThis = 0, outThis = 0, inLast = 0, outLast = 0;

    for (const t of transactions) {
      if (!t.createdAt) continue;
      const d     = new Date(t.createdAt);
      const m     = d.getMonth();
      const y     = d.getFullYear();
      const amt   = Math.abs(txnAmount(t));
      const isIn  = txnDirection(t) === "in";

      if (y === thisYear && m === thisMonth) {
        if (isIn) inThis += amt; else outThis += amt;
      } else if (y === lastYear && m === lastMonth) {
        if (isIn) inLast += amt; else outLast += amt;
      }
    }

    const spendDelta = outThis - outLast;

    return { inThis, outThis, spendDelta };
  }, [transactions]);
};

const QUICK_ACTIONS = [
  { icon: <PlusCircle size={18}/>, label: "Add Money",  path: (uid: string) => `/wallet/${uid}` },
  { icon: <ArrowUpRight size={18}/>, label: "Transfer", path: (uid: string) => `/Transfer/${uid}` },
  { icon: <ArrowDownLeft size={18}/>, label: "Receive", path: (uid: string) => `/ReceiveTransfer/${uid}` },
  { icon: <CreditCard size={18}/>, label: "Cards",      path: (uid: string) => `/PaymentMethods/${uid}` },
    { icon: <CreditCard size={18}/>, label: "NewCard",      path: (uid: string) => `/Cards/${uid}` },

  { icon: <Zap size={18}/>, label: "Pay",               path: (uid: string) => `/pay/${uid}` },
  { icon: <Globe size={18}/>, label: "Convert",         path: (uid: string) => `/convert/${uid}` },
  { icon: <TrendingUp size={18}/>, label: "Invest",     path: (uid: string) => `/invest/${uid}` },
];

const PUBLIC_LINKS = [
  { label: "What's New",      path: "/whats-new" },
  { label: "World of Cosmoola", path: "/world" },
  { label: "News",            path: "/news" },
  { label: "FAQs",            path: "/faqs" },
  { label: "Privacy Policy",  path: "/privacy" },
  { label: "Terms of Service",path: "/terms" },
];
const SOCIALS = [
  { label: "Instagram", url: "https://instagram.com/cosmoola" },
  { label: "TikTok",    url: "https://tiktok.com/@cosmoola" },
  { label: "YouTube",   url: "https://youtube.com/@cosmoola" },
  { label: "LinkedIn",  url: "https://linkedin.com/company/cosmoola" },
];
const navItems = (uid: string) => [
  { label: "Profile",       sub: "View & edit your info",    path: "/profile/"+uid,         icon: <UserCheck size={18}/> },
  { label: "My Account",    sub: "Balance, limits & settings",path: "/account/"+uid,        icon: <Wallet size={18}/> },
  { label: "Cards",         sub: "Manage your cards",        path: "/PaymentMethods/"+uid,  icon: <CreditCard size={18}/> },
  { label: "Transactions",  sub: "Full history",             path: "/transactions/"+uid,    icon: <ClipboardList size={18}/> },
  { label: "Management",    sub: "Admin & operations",       path: "/merchantoverview/"+uid,      icon: <BarChart2 size={18}/> },
  { label: "Verify Profile",sub: "Complete KYC",             path: "/VerifyProfile",        icon: <ShieldCheck size={18}/> },
  { label: "Settings",      sub: "Privacy & security",       path: "/ProfileSettings/"+uid, icon: <Settings size={18}/> },
];

/* ════════════════════════════════════════════════════════════
   CONTACT MODAL
════════════════════════════════════════════════════════════ */
const ContactModal: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const [form, setForm]     = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent]     = useState(false);

  const submit = async () => {
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try {
      await addDoc(collection(db, "contactMessages"), { ...form, createdAt: serverTimestamp() });
      setSent(true);
      setTimeout(() => { setSent(false); onClose(); setForm({ name: "", email: "", subject: "", message: "" }); }, 2200);
    } catch (e) { console.error(e); } finally { setSending(false); }
  };

  const inp = (l: string, k: keyof typeof form, t?: string) => (
    <div>
      <label style={{ fontSize: 9, color: D.muted, letterSpacing: "0.2em", display: "block", marginBottom: 6 }}>{l}</label>
      <input type={t || "text"} value={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
        style={{ width: "100%", background: "rgba(255,255,255,0.03)", border: "1px solid "+D.border, borderRadius: 0, padding: "12px 14px", color: D.offwhite, fontSize: 13, fontFamily: "'Montserrat',sans-serif", outline: "none" }}/>
    </div>
  );

  return (
    <AnimatePresence>{open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
        onClick={onClose}>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }} transition={{ duration: 0.3 }}
          style={{ background: D.bg, border: "1px solid "+D.border, width: "100%", maxWidth: 480, padding: "40px 32px", position: "relative" }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "none", border: "none", color: D.white, cursor: "pointer" }}><X size={20}/></button>
          {sent ? (
            <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} style={{ textAlign: "center", padding: "40px 0" }}>
              <CheckCircle size={48} color={D.white} style={{ marginBottom: 16 }}/>
              <div style={{ fontSize: 22, fontWeight: 300, color: D.white, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.06em" }}>Message Sent</div>
              <div style={{ fontSize: 12, color: D.muted, marginTop: 8 }}>We'll get back to you shortly.</div>
            </motion.div>
          ) : (
            <>
              <div style={{ fontSize: 24, fontWeight: 300, color: D.white, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.08em", marginBottom: 6 }}>Contact Us</div>
              <div style={{ fontSize: 12, color: D.muted, marginBottom: 28 }}>We'd love to hear from you.</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                {inp("NAME", "name")}{inp("EMAIL", "email", "email")}{inp("SUBJECT", "subject")}
                <div>
                  <label style={{ fontSize: 9, color: D.muted, letterSpacing: "0.2em", display: "block", marginBottom: 6 }}>MESSAGE</label>
                  <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={4}
                    style={{ width: "100%", background: "rgba(255,255,255,0.03)", border: "1px solid "+D.border, borderRadius: 0, padding: "12px 14px", color: D.offwhite, fontSize: 13, fontFamily: "'Montserrat',sans-serif", outline: "none", resize: "vertical" }}/>
                </div>
                <button onClick={submit} disabled={sending || !form.name || !form.email || !form.message}
                  style={{ width: "100%", background: D.white, color: D.bg, border: "none", borderRadius: 0, padding: "14px 0", fontWeight: 500, fontSize: 12, letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif", cursor: sending ? "wait" : "pointer", opacity: (!form.name || !form.email || !form.message) ? 0.4 : 1, marginTop: 4 }}>
                  {sending ? "SENDING..." : "SEND MESSAGE"}
                </button>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    )}</AnimatePresence>
  );
};

/* ════════════════════════════════════════════════════════════
   MENU OVERLAY
════════════════════════════════════════════════════════════ */
const MenuOverlay: React.FC<{ open: boolean; onClose: () => void; navigate: (p: string) => void; loggedIn: boolean; userId: string }> = ({ open, onClose, navigate, loggedIn, userId }) => {
  const bg  = loggedIn ? L.bg : D.bg;
  const txt = loggedIn ? L.text : D.offwhite;
  const bdr = loggedIn ? L.border : D.border;
  const mut = loggedIn ? L.textSoft : D.muted;
  const sub = loggedIn ? L.muted : D.subtle;
  return (
    <AnimatePresence>{open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: "fixed", inset: 0, background: loggedIn ? "rgba(0,0,0,0.4)" : "rgba(0,0,0,0.92)", backdropFilter: "blur(10px)", zIndex: 9998 }}
        onClick={onClose}>
        <motion.div initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", stiffness: 300, damping: 30 }}
          style={{ position: "absolute", right: 0, top: 0, bottom: 0, width: "100%", maxWidth: 420, background: bg, borderLeft: "1px solid "+bdr, overflowY: "auto", padding: "28px 32px", display: "flex", flexDirection: "column" }}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <button onClick={onClose} style={{ alignSelf: "flex-end", background: "none", border: "1px solid "+bdr, color: loggedIn ? L.text : D.white, cursor: "pointer", marginBottom: 24, width: 44, height: 44, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "50%" }}><X size={20}/></button>
          {PUBLIC_LINKS.map((l, i) => (
            <motion.button key={l.label} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
              onClick={() => { onClose(); navigate(l.path); }}
              style={{ background: "none", border: "none", borderBottom: "1px solid "+bdr, padding: "18px 0", cursor: "pointer", textAlign: "left", width: "100%", fontFamily: "'Cormorant Garamond',serif" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={{ fontSize: 20, fontWeight: 300, color: txt, letterSpacing: "0.02em" }}>{l.label}</span>
                <ChevronRight size={18} color={sub}/>
              </div>
            </motion.button>
          ))}
          {loggedIn && (<>
            <div style={{ height: 1, background: bdr, margin: "24px 0 16px" }}/>
            <div style={{ fontSize: 10, color: mut, letterSpacing: "0.2em", marginBottom: 12 }}>ACCOUNT</div>
            {navItems(userId).map((it, i) => (
              <motion.button key={it.label} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 + i * 0.04 }}
                onClick={() => { onClose(); navigate(it.path); }}
                style={{ background: "none", border: "none", borderBottom: "1px solid "+bdr, padding: "14px 0", cursor: "pointer", textAlign: "left", width: "100%", display: "flex", alignItems: "center", gap: 14, fontFamily: "inherit" }}>
                <span style={{ color: mut }}>{it.icon}</span>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 400, color: txt, fontFamily: "'Cormorant Garamond',serif" }}>{it.label}</div>
                  <div style={{ fontSize: 11, color: mut, marginTop: 1 }}>{it.sub}</div>
                </div>
              </motion.button>
            ))}
          </>)}
          <div style={{ marginTop: "auto", paddingTop: 32 }}>
            <div style={{ fontSize: 10, color: mut, letterSpacing: "0.2em", marginBottom: 12 }}>COSMOOLA SERVICES</div>
            <div style={{ fontSize: 13, color: txt, cursor: "pointer", marginBottom: 8, textDecoration: "underline", textUnderlineOffset: 3 }} onClick={() => { onClose(); navigate("/services"); }}>Discover Our Services</div>
            <div style={{ fontSize: 13, color: txt, cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }} onClick={() => { onClose(); navigate("/support"); }}>Book an Appointment</div>
          </div>
        </motion.div>
      </motion.div>
    )}</AnimatePresence>
  );
};

/* ════════════════════════════════════════════════════════════
   FOOTER
════════════════════════════════════════════════════════════ */
const FL: React.FC<{ l: string; o: () => void }> = ({ l, o }) => (
  <div onClick={o} style={{ fontSize: 13, color: D.offwhite, cursor: "pointer", marginBottom: 10, textDecoration: "underline", textUnderlineOffset: 3, lineHeight: 1.6 }}>{l}</div>
);
const GucciFooter: React.FC<{ navigate: (p: string) => void }> = ({ navigate }) => (
  <footer style={{ background: D.bg, borderTop: "1px solid "+D.border, padding: "48px 24px 0" }}>
    <div style={{ maxWidth: 1200, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 40, paddingBottom: 40, borderBottom: "1px solid "+D.border }}>
      <div>
        <div style={{ fontSize: 10, color: D.muted, letterSpacing: "0.2em", marginBottom: 16 }}>COSMOOLA SERVICES</div>
        <FL l="Discover Our Services" o={() => navigate("/services")}/>
        <FL l="Book an Appointment"   o={() => navigate("/support")}/>
        <FL l="Manage Your Cosmoola Account" o={() => navigate("/Cards/"+User)}/>
      </div>
      <div>
        <div style={{ fontSize: 10, color: D.muted, letterSpacing: "0.2em", marginBottom: 16 }}>COMPANY</div>
        <FL l="Privacy Policy"                    o={() => navigate("/privacy")}/>
        <FL l="Terms of Service"                  o={() => navigate("/terms")}/>
        <FL l="Vulnerability Disclosure Policy"   o={() => navigate("/security")}/>
      </div>
      <div>
        <div style={{ fontSize: 10, color: D.muted, letterSpacing: "0.2em", marginBottom: 16 }}>FOLLOW US</div>
        {SOCIALS.map(s => (
          <div key={s.label} onClick={() => window.open(s.url, "_blank")}
            style={{ fontSize: 13, color: D.offwhite, cursor: "pointer", marginBottom: 10, textDecoration: "underline", textUnderlineOffset: 3, lineHeight: 1.6, display: "flex", alignItems: "center", gap: 6 }}>
            {s.label}<ExternalLink size={11} color={D.muted}/>
          </div>
        ))}
      </div>
      <div>
        <div style={{ fontSize: 10, color: D.muted, letterSpacing: "0.2em", marginBottom: 16 }}>COUNTRY/REGION</div>
        <div style={{ fontSize: 13, color: D.offwhite, textDecoration: "underline", textUnderlineOffset: 3, cursor: "pointer" }}>International</div>
      </div>
    </div>
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "20px 0" }}>
      <div style={{ fontSize: 11, color: D.muted }}>{"\u00a9"} 2025 {"\u2013"} {new Date().getFullYear()} Cosmoola Technologies {"\u2014"} All rights reserved.</div>
    </div>
    <div style={{ overflow: "hidden", padding: "40px 0 0", borderTop: "1px solid "+D.border }}>
      <div style={{ fontSize: "min(18vw,220px)", fontWeight: 300, color: D.white, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.15em", textAlign: "center", lineHeight: 1, paddingBottom: 20, whiteSpace: "nowrap" }}>COSMOOLA</div>
    </div>
  </footer>
);

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const CosmoolaHome: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { user }   = useSelector((s: RootState) => s.auth);
  const userId     = user?.userId ?? "";

  /* ── Real data from Redux ── */
  const { wallet, savedCards, transactions } = useSelector((s: RootState) => s.transactions);

  const [balanceHidden, setBalanceHidden] = useState(false);
  const [menuOpen, setMenuOpen]           = useState(false);
  const [contactOpen, setContactOpen]     = useState(false);
  const [copied, setCopied]               = useState(false);
  const [loaded, setLoaded]               = useState(false);

  /* ── Start real-time listeners when user is logged in ── */
  useEffect(() => {
    dispatch(fetchUserData());
  }, [dispatch]);

  useEffect(() => {
    if (userId) {
      dispatch(listenToUserWallet(userId));
      dispatch(listenToUserSavedCards(userId));
      dispatch(listenToUserTransactions(userId));
    }
    return () => { dispatch(cleanupWalletListeners()); };
  }, [userId, dispatch]);

  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  /* ── Derived values ── */
  const balance      = wallet?.balance ?? 0;
  const defaultCard  = savedCards.find(c => c.isDefault) ?? savedCards[0] ?? null;
  const recentTxns   = transactions.slice(0, 5);
  const { inThis, outThis, spendDelta } = useMonthlyStats(transactions);

  const photo = user?.profilePicture ?? null;
  const ini   = ((user?.firstName?.[0] ?? "") + (user?.lastName?.[0] ?? "")).toUpperCase() || "U";
  const name  = user ? maskName(user.firstName) + " " + maskName(user.lastName) : "";
  const email = maskEmail(user?.emailAddress ?? user?.email ?? "");
  const isV   = !!user?.verifyEmail;

  const copyRef = async () => {
    await navigator.clipboard.writeText("https://cosmoola.com/ref/" + user?.username);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };
  const doLogout = async () => {
    await dispatch(logout()).unwrap();
    navigate("/AuthSwitch");
  };

  /* ════════════════════════════════════════════════════════
     HEADER
  ════════════════════════════════════════════════════════ */
  const Hdr = () => (
    <header style={{ position: "sticky", top: 0, zIndex: 100, background: "rgba(0,0,0,0.95)", backdropFilter: "blur(12px)", borderBottom: "1px solid "+D.border, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", height: 58 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <img src={logo} alt="Cosmoola" style={{ width: 30, height: 30, borderRadius: 0, objectFit: "cover", cursor: "pointer" }} onClick={() => navigate("/")}/>
        <button onClick={() => setContactOpen(true)} className="desk-contact"
          style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, color: D.offwhite, fontSize: 11, fontWeight: 400, fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.03em" }}>
          <Plus size={13} strokeWidth={1.5}/><span>Contact Us</span>
        </button>
      </div>
      <div style={{ position: "absolute", left: "50%", transform: "translateX(-50%)", cursor: "pointer" }} onClick={() => navigate("/")}>
        <span className="brand-center" style={{ color: D.white, fontWeight: 300, letterSpacing: "0.35em", fontFamily: "'Cormorant Garamond',serif" }}>COSMOOLA</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
        {user && <button style={{ background: "none", border: "none", cursor: "pointer", color: D.offwhite, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => navigate("/Notifications")}><Bell size={17} strokeWidth={1.5}/></button>}
        <button style={{ background: "none", border: "none", cursor: "pointer", color: D.offwhite, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => navigate("/AuthSwitch")}><User size={17} strokeWidth={1.5}/></button>
        <button style={{ background: "none", border: "none", cursor: "pointer", color: D.offwhite, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => navigate("/search")}><Search size={17} strokeWidth={1.5}/></button>
        <button onClick={() => setMenuOpen(true)} style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "1px solid "+D.offwhite, borderRadius: 0, padding: "6px 14px", cursor: "pointer", color: D.offwhite, marginLeft: 2 }}>
          <Menu size={15} strokeWidth={1.5}/><span className="menu-label" style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif" }}>MENU</span>
        </button>
      </div>
    </header>
  );

  /* ════════════════════════════════════════════════════════
     LOGGED OUT
  ════════════════════════════════════════════════════════ */
  if (!user) return (
    <div style={{ minHeight: "100vh", background: D.bg, color: D.white, fontFamily: "'Montserrat','Cormorant Garamond',system-ui,sans-serif" }}>
      <style>{CSS}</style>
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)}/>
      <MenuOverlay open={menuOpen} onClose={() => setMenuOpen(false)} navigate={navigate} loggedIn={false} userId="guest"/>
      <Hdr/>
      <motion.div style={{ position: "relative", width: "100%", height: "85vh", minHeight: 500, maxHeight: 900, overflow: "hidden" }} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }}>
        <img src={watchImg} alt="Cosmoola" style={{ width: "100%", height: "100%", objectFit: "cover", filter: "grayscale(100%) contrast(1.1)" }}/>
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top,rgba(0,0,0,0.8) 0%,rgba(0,0,0,0.1) 35%,transparent 55%)" }}/>
        <div style={{ position: "absolute", bottom: 60, left: 0, right: 0, textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
          <h1 style={{ fontSize: "clamp(32px,5vw,56px)", fontWeight: 300, color: D.white, margin: 0, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.06em" }}>Cosmoola</h1>
          <button style={{ fontSize: 12, color: D.white, letterSpacing: "0.25em", fontWeight: 400, padding: "13px 34px", border: "1px solid "+D.white, cursor: "pointer", background: "transparent", fontFamily: "'Montserrat',sans-serif" }} onClick={() => navigate("/AuthSwitch")}>EXPLORE NOW</button>
        </div>
      </motion.div>
      <motion.div style={{ maxWidth: 500, margin: "0 auto", padding: "56px 24px 48px", textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 18 }} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }}>
        <h2 style={{ fontSize: 13, fontWeight: 500, letterSpacing: "0.25em", color: D.white, margin: 0, fontFamily: "'Montserrat',sans-serif" }}>SEE NOW, JOIN NOW</h2>
        <p style={{ fontSize: 15, color: D.muted, lineHeight: 1.7, margin: 0, fontFamily: "'Cormorant Garamond',serif" }}>The modern payment platform {"\u2014"} smart money, zero friction. Open your account and experience luxury finance.</p>
        <button style={{ width: "100%", maxWidth: 400, background: "transparent", color: D.white, border: "1px solid "+D.white, borderRadius: 0, padding: "14px 0", fontWeight: 500, fontSize: 11, cursor: "pointer", letterSpacing: "0.2em", fontFamily: "'Montserrat',sans-serif" }} onClick={() => navigate("/AuthSwitch")}>GET STARTED</button>
        <button style={{ width: "100%", maxWidth: 400, background: "transparent", color: D.muted, border: "1px solid "+D.border, borderRadius: 0, padding: "14px 0", fontWeight: 400, fontSize: 11, cursor: "pointer", fontFamily: "'Montserrat',sans-serif", letterSpacing: "0.15em" }} onClick={() => navigate("/AuthSwitch")}>SIGN IN</button>
      </motion.div>
      <GucciFooter navigate={navigate}/>
    </div>
  );

  /* ════════════════════════════════════════════════════════
     LOGGED IN
  ════════════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: L.bg, color: L.text, fontFamily: "'Montserrat','Cormorant Garamond',system-ui,sans-serif" }}>
      <style>{CSS}</style>
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)}/>
      <MenuOverlay open={menuOpen} onClose={() => setMenuOpen(false)} navigate={navigate} loggedIn={true} userId={userId}/>

      {/* ══ MOBILE ══ */}
      <div className="mob-only">
        <Hdr/>

        {/* Manage Account */}
        <div style={{ margin: "16px 16px 0", background: L.surface, border: "1px solid "+L.border, padding: "18px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }} onClick={() => navigate("/account/"+userId)}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src={logo} alt="C" style={{ width: 28, height: 28, borderRadius: 0, objectFit: "cover" }}/>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: L.text, letterSpacing: "0.03em" }}>Manage Your Cosmoola Account</div>
              <div style={{ fontSize: 10, color: L.textSoft, marginTop: 2 }}>Cards, limits & settings</div>
            </div>
          </div>
          <ChevronRight size={16} color={L.muted}/>
        </div>

        {/* Balance — REAL */}
        <motion.div style={{ margin: "12px 16px 0", background: L.surface, border: "1px solid "+L.border, padding: "26px 20px" }}
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <p style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.15em", marginBottom: 8 }}>TOTAL BALANCE</p>
              <p style={{ fontSize: 34, fontWeight: 300, color: L.text, letterSpacing: "-0.02em", fontFamily: "'Cormorant Garamond',serif" }}>
                {balanceHidden ? EUR + " \u00b7\u00b7\u00b7\u00b7\u00b7" : fmt(balance)}
              </p>
            </div>
            <button style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }} onClick={() => setBalanceHidden(v => !v)}>
              {balanceHidden ? <Eye size={18} color={L.muted}/> : <EyeOff size={18} color={L.muted}/>}
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 20, paddingTop: 16, borderTop: "1px solid "+L.border }}>
            <LStatPill label="In"  value={fmt(inThis)}  up={true}/>
            <div style={{ width: 1, height: 28, background: L.border }}/>
            <LStatPill label="Out" value={fmt(outThis)} up={false}/>
          </div>
        </motion.div>

        {/* Card chip — REAL default card */}
        <motion.div style={{ margin: "12px 16px 0", background: D.bg, border: "1px solid "+D.border2, padding: "20px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: loaded ? 1 : 0, y: loaded ? 0 : 16 }} transition={{ duration: 0.4, delay: 0.1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {defaultCard ? (
              <div style={{ width: 38, height: 26, borderRadius: 4, background: BRAND_COLORS[defaultCard.brand?.toLowerCase()] ? BRAND_COLORS[defaultCard.brand.toLowerCase()].bg : BRAND_COLORS.default.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <span style={{ fontSize: 6, color: "#fff", fontWeight: 800, letterSpacing: "0.05em" }}>
                  {(BRAND_COLORS[defaultCard.brand?.toLowerCase()] ?? BRAND_COLORS.default).label}
                </span>
              </div>
            ) : (
              <img src={logo} alt="C" style={{ width: 28, height: 28, borderRadius: 0, objectFit: "cover" }}/>
            )}
            <div>
              <div style={{ fontSize: 9, letterSpacing: "0.25em", color: D.muted, marginBottom: 4 }}>
                {defaultCard ? `${defaultCard.brand.toUpperCase()} CARD` : "COSMOOLA CARD"}
              </div>
              <div style={{ fontSize: 13, color: D.offwhite, fontFamily: "monospace", fontWeight: 500 }}>
                {defaultCard ? `\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ${defaultCard.last4}` : "\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ••••"}
              </div>
              {defaultCard && (
                <div style={{ fontSize: 9, color: D.muted, marginTop: 2 }}>
                  Exp {String(defaultCard.expMonth).padStart(2, "0")}/{defaultCard.expYear}
                </div>
              )}
            </div>
          </div>
          <LVPill ok={isV}/>
        </motion.div>

        {/* Quick actions */}
        <div style={{ display: "flex", gap: 10, padding: "16px 16px 0", overflowX: "auto", scrollbarWidth: "none" }}>
          {QUICK_ACTIONS.map((a, i) => (
            <motion.div key={a.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 + i * 0.05 }}>
              <LQBtn icon={a.icon} label={a.label} onClick={() => navigate(a.path(userId))}/>
            </motion.div>
          ))}
        </div>

        {/* Transactions — REAL */}
        <div style={{ padding: "16px 16px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <span style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", fontWeight: 500 }}>RECENT</span>
            <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: L.muted, fontFamily: "inherit" }} onClick={() => navigate("/transactions/"+userId)}>See all {"\u2192"}</button>
          </div>
          {recentTxns.length === 0 ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <p style={{ fontSize: 12, color: L.muted }}>No transactions yet</p>
            </div>
          ) : (
            recentTxns.map(t => <LTRow key={t.id} t={t}/>)
          )}
        </div>

        {/* Account */}
        <div style={{ padding: "16px 16px 0" }}>
          <span style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", fontWeight: 500, display: "block", marginBottom: 12 }}>ACCOUNT</span>
          <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "16px 0", borderBottom: "1px solid "+L.border }}>
            <Av photo={photo} ini={ini} size={54} light/>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: L.text }}>{name}</div>
              <div style={{ fontSize: 11, color: L.textSoft, marginTop: 2 }}>{email}</div>
              <LVPill ok={isV}/>
            </div>
            <button style={{ background: "transparent", border: "1px solid "+L.border, width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: L.muted, flexShrink: 0 }} onClick={() => navigate("/EditProfile/"+userId)}>
              <Pencil size={14}/>
            </button>
          </div>
        </div>

        {user?.username && (
          <div style={{ padding: "0 16px" }}>
            <LRCard u={user.username} copied={copied} onCopy={copyRef}/>
          </div>
        )}

        <div style={{ padding: "16px 16px 0" }}>
          {navItems(userId).map((it, i) => (
            <motion.div key={it.label} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}>
              <LNRow icon={it.icon} label={it.label} sub={it.sub} onClick={() => navigate(it.path)}/>
            </motion.div>
          ))}
          <LNRow icon={<LogOut size={18}/>} label="Log Out" sub="Sign out" danger onClick={doLogout}/>
        </div>

        <div style={{ height: 20 }}/>
        <GucciFooter navigate={navigate}/>
      </div>

      {/* ══ DESKTOP ══ */}
      <div className="desk-only">
        <Hdr/>
        <div style={{ padding: "0 56px 60px", maxWidth: 1400, margin: "0 auto" }}>

          {/* Top strip */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "28px 0 24px", borderBottom: "1px solid "+L.border, marginBottom: 28 }}>
            <div>
              <p style={{ fontSize: 11, color: L.textSoft, letterSpacing: "0.15em", marginBottom: 4 }}>Good afternoon,</p>
              <p style={{ fontSize: 24, fontWeight: 300, color: L.text, fontFamily: "'Cormorant Garamond',serif", letterSpacing: "0.03em" }}>{name}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <button onClick={() => navigate("/merchantoverview/"+userId)} style={{ display: "flex", alignItems: "center", gap: 8, background: L.surface, border: "1px solid "+L.border, borderRadius: 0, padding: "8px 16px", cursor: "pointer", color: L.text, fontSize: 11, fontWeight: 500, letterSpacing: "0.05em", fontFamily: "'Montserrat',sans-serif" }}>
                <Settings size={14}/> Manage Account
              </button>
              <Av photo={photo} ini={ini} size={36} light/>
            </div>
          </div>

          {/* Metrics — REAL */}
          <motion.div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 28 }} className="desk-metrics"
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            {[
              {
                l: "Total Balance",
                v: balanceHidden ? EUR + " •••••" : fmt(balance),
                s: inThis > 0 ? `+${fmt(inThis)} this month` : "No top-ups this month",
                u: true,
                i: <Wallet size={18}/>,
              },
              {
                l: "Monthly Spending",
                v: fmt(outThis),
                s: spendDelta === 0 ? "Same as last month" : `${spendDelta > 0 ? "+" : ""}${fmt(spendDelta)} vs last month`,
                u: spendDelta <= 0,
                i: <CreditCard size={18}/>,
              },
              {
                l: "Transactions",
                v: String(transactions.length),
                s: `${recentTxns.length} recent`,
                u: null as boolean | null,
                i: <TrendingUp size={18}/>,
              },
              {
                l: "Saved Cards",
                v: String(savedCards.length),
                s: defaultCard ? `Default: ••••${defaultCard.last4}` : "No cards saved",
                u: null as boolean | null,
                i: <Zap size={18}/>,
              },
            ].map(m => (
              <motion.div key={m.l} whileHover={{ y: -3 } as any} transition={{ duration: 0.15 }}>
                <LMCard label={m.l} value={m.v} sub={m.s} up={m.u} icon={m.i}/>
              </motion.div>
            ))}
          </motion.div>

          {/* Grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 20 }} className="desk-grid">
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* Card — REAL default card */}
              <motion.div style={{ background: D.bg, border: "1px solid "+D.border2, padding: "24px 22px" }}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                <p style={{ fontSize: 10, color: D.muted, letterSpacing: "0.2em", marginBottom: 16 }}>
                  {defaultCard ? "YOUR CARD" : "YOUR CARD"}
                </p>
                <div style={{ background: "linear-gradient(135deg,#111 0%,#060606 100%)", border: "1px solid "+D.border2, padding: "26px 24px", marginBottom: 16 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 28 }}>
                    {defaultCard ? (
                      <div style={{ width: 40, height: 28, borderRadius: 4, background: (BRAND_COLORS[defaultCard.brand?.toLowerCase()] ?? BRAND_COLORS.default).bg, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <span style={{ fontSize: 7, color: "#fff", fontWeight: 800 }}>{(BRAND_COLORS[defaultCard.brand?.toLowerCase()] ?? BRAND_COLORS.default).label}</span>
                      </div>
                    ) : (
                      <img src={logo} alt="C" style={{ width: 32, height: 32, borderRadius: 0, objectFit: "cover" }}/>
                    )}
                    <span style={{ fontSize: 13, fontWeight: 400, letterSpacing: "0.3em", color: D.white, fontFamily: "'Cormorant Garamond',serif" }}>COSMOOLA</span>
                  </div>
                  <div style={{ fontSize: 17, fontFamily: "monospace", color: D.offwhite, letterSpacing: "0.15em", marginBottom: 22 }}>
                    {defaultCard
                      ? `\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ${defaultCard.last4}`
                      : "\u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 \u2022\u2022\u2022\u2022 ••••"}
                  </div>
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 9, color: D.muted, letterSpacing: "0.15em", marginBottom: 4 }}>CARD HOLDER</div>
                      <div style={{ fontSize: 12, color: D.offwhite, fontWeight: 500 }}>{name.toUpperCase()}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 9, color: D.muted, letterSpacing: "0.15em", marginBottom: 4 }}>EXPIRES</div>
                      <div style={{ fontSize: 12, color: D.offwhite, fontWeight: 500 }}>
                        {defaultCard ? `${String(defaultCard.expMonth).padStart(2, "0")} / ${String(defaultCard.expYear).slice(-2)}` : "—— / ——"}
                      </div>
                    </div>
                    <LVPill ok={isV}/>
                  </div>
                </div>
                {/* Quick actions on card */}
                <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
                  {QUICK_ACTIONS.slice(0, 4).map(a => (
                    <QBtnDark key={a.label} icon={a.icon} label={a.label} onClick={() => navigate(a.path(userId))}/>
                  ))}
                </div>
                {/* No card CTA */}
                {!defaultCard && (
                  <button onClick={() => navigate(`/PaymentMethods/${userId}`)}
                    style={{ width: "100%", marginTop: 12, padding: "11px 0", background: "transparent", border: "1px dashed "+D.border2, color: D.muted, cursor: "pointer", fontSize: 10, letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    <Plus size={13}/> ADD A CARD
                  </button>
                )}
              </motion.div>

              {user?.username && (
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                  <LRCard u={user.username} copied={copied} onCopy={copyRef}/>
                </motion.div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              {/* Transactions — REAL */}
              <motion.div style={{ background: L.surface, border: "1px solid "+L.border, padding: "24px 22px" }}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                  <p style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em" }}>RECENT TRANSACTIONS</p>
                  <button style={{ background: "none", border: "none", cursor: "pointer", fontSize: 11, color: L.muted, fontFamily: "inherit" }} onClick={() => navigate("/transactions/"+userId)}>View all {"\u2192"}</button>
                </div>
                {recentTxns.length === 0 ? (
                  <div style={{ padding: "32px 0", textAlign: "center" }}>
                    <CreditCard size={28} color={L.muted} style={{ marginBottom: 10 }}/>
                    <p style={{ fontSize: 12, color: L.muted }}>No transactions yet</p>
                    <button onClick={() => navigate(`/Wallet/${userId}`)} style={{ marginTop: 12, background: D.bg, color: D.white, border: "none", padding: "10px 20px", cursor: "pointer", fontSize: 10, letterSpacing: "0.15em", fontFamily: "'Montserrat',sans-serif" }}>
                      ADD MONEY
                    </button>
                  </div>
                ) : (
                  recentTxns.map(t => <LTRow key={t.id} t={t}/>)
                )}
              </motion.div>

              {/* Account */}
              <motion.div style={{ background: L.surface, border: "1px solid "+L.border, padding: "24px 22px" }}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <p style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.2em", marginBottom: 16 }}>ACCOUNT</p>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
                  <Av photo={photo} ini={ini} size={52} light/>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 15, fontWeight: 500, color: L.text }}>{name}</div>
                    <div style={{ fontSize: 11, color: L.textSoft, marginTop: 2 }}>{email}</div>
                    <LVPill ok={isV}/>
                  </div>
                  <button style={{ background: "transparent", border: "1px solid "+L.border, padding: "7px 16px", cursor: "pointer", color: L.textSoft, fontSize: 11, fontWeight: 500, display: "flex", alignItems: "center", gap: 6, flexShrink: 0, fontFamily: "inherit", letterSpacing: "0.08em" }} onClick={() => navigate("/EditProfile/"+userId)}>
                    <Pencil size={13}/> Edit
                  </button>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 24px", marginTop: 4 }}>
                  <LDCell l="User ID"      v={"#" + userId.slice(0, 8).toUpperCase()}/>
                  <LDCell l="Username"     v={"@" + (user?.username ?? "\u2014")}/>
                  <LDCell l="Phone"        v={user?.phoneNumber ?? "\u2014"}/>
                  <LDCell l="Member Since" v={user?.joinDate ? String(new Date(user.joinDate).getFullYear()) : "\u2014"}/>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
        <GucciFooter navigate={navigate}/>
      </div>
    </div>
  );
};

export default CosmoolaHome;

/* ════════════════════════════════════════════════════════════
   SUB-COMPONENTS
════════════════════════════════════════════════════════════ */

const Av: React.FC<{ photo: string | null; ini: string; size: number; light?: boolean }> = ({ photo, ini, size, light }) => (
  <div style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", background: light ? L.surface2 : D.surface2, border: "1px solid "+(light ? L.border : D.border2), flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
    {photo ? <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }}/> : <span style={{ color: light ? L.text : D.white, fontWeight: 400, fontSize: size * 0.36, fontFamily: "'Cormorant Garamond',serif" }}>{ini}</span>}
  </div>
);

const LVPill: React.FC<{ ok: boolean }> = ({ ok }) => (
  <div style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "3px 10px", background: ok ? "rgba(0,0,0,0.05)" : "rgba(0,0,0,0.02)", border: "1px solid "+(ok ? L.text : L.border), marginTop: 6 }}>
    {ok ? <ShieldCheck size={10} color={L.text}/> : <Shield size={10} color={L.muted}/>}
    <span style={{ fontSize: 9, fontWeight: 500, letterSpacing: "0.15em", color: ok ? L.text : L.muted }}>{ok ? "VERIFIED" : "UNVERIFIED"}</span>
  </div>
);

const LStatPill: React.FC<{ label: string; value: string; up: boolean }> = ({ label, value, up }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <span style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.12em", fontFamily: "'Cormorant Garamond',serif" }}>{label.toUpperCase()}</span>
    <span style={{ fontSize: 14, fontWeight: 600, color: up ? L.text : L.muted }}>{up ? "\u2191" : "\u2193"} {value}</span>
  </div>
);

const LQBtn: React.FC<{ icon: React.ReactNode; label: string; onClick: () => void }> = ({ icon, label, onClick }) => (
  <motion.button onClick={onClick}
    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, background: L.surface, border: "1px solid "+L.border, padding: "14px 12px", cursor: "pointer", minWidth: 58, color: L.text, fontFamily: "inherit" }}
    whileHover={{ background: L.surface2, borderColor: L.text } as any}
    whileTap={{ scale: 0.95 } as any}>
    <span style={{ color: L.text }}>{icon}</span>
    <span style={{ fontSize: 9, color: L.textSoft, letterSpacing: "0.12em", fontFamily: "'Cormorant Garamond',serif" }}>{label.toUpperCase()}</span>
  </motion.button>
);

const QBtnDark: React.FC<{ icon: React.ReactNode; label: string; onClick: () => void }> = ({ icon, label, onClick }) => (
  <motion.button onClick={onClick}
    style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, background: "transparent", border: "1px solid "+D.border, padding: "14px 12px", cursor: "pointer", minWidth: 58, color: D.white, fontFamily: "inherit" }}
    whileHover={{ background: "rgba(255,255,255,0.06)", borderColor: D.white } as any}
    whileTap={{ scale: 0.95 } as any}>
    <span style={{ color: D.offwhite }}>{icon}</span>
    <span style={{ fontSize: 9, color: D.muted, letterSpacing: "0.12em", fontFamily: "'Cormorant Garamond',serif" }}>{label.toUpperCase()}</span>
  </motion.button>
);

/* Transaction row — renders a REAL Transaction object */
const LTRow: React.FC<{ t: Transaction }> = ({ t }) => {
  const dir    = txnDirection(t);
  const amount = txnAmount(t);
  const label  = txnLabel(t);
  const time   = fmtDate(t.createdAt);

  const statusDot = t.status === "pending" ? (
    <span style={{ fontSize: 9, color: "#b8860b", background: "#fffbf0", border: "1px solid #f0e8c8", padding: "1px 6px", letterSpacing: "0.1em" }}>PENDING</span>
  ) : t.status === "failed" ? (
    <span style={{ fontSize: 9, color: "#B22222", background: "#fff0f0", border: "1px solid #f0c8c8", padding: "1px 6px", letterSpacing: "0.1em" }}>FAILED</span>
  ) : null;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 0", borderBottom: "1px solid "+L.border }}>
      <div style={{ width: 38, height: 38, flexShrink: 0, background: dir === "in" ? "rgba(0,0,0,0.03)" : "rgba(0,0,0,0.02)", border: "1px solid "+L.border, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {dir === "in" ? <ArrowDownLeft size={16} color={L.text}/> : <ArrowUpRight size={16} color={L.muted}/>}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: L.text, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
        <div style={{ fontSize: 11, color: L.textSoft, marginTop: 2, display: "flex", alignItems: "center", gap: 8 }}>
          {time}
          {statusDot}
        </div>
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: dir === "in" ? L.text : L.textSoft, fontFamily: "'Cormorant Garamond',serif", flexShrink: 0 }}>
        {dir === "in" ? "+" : ""}{fmt(amount)}
      </div>
    </div>
  );
};

const LNRow: React.FC<{ icon: React.ReactNode; label: string; sub: string; onClick: () => void; danger?: boolean }> = ({ icon, label, sub, onClick, danger }) => (
  <motion.button onClick={onClick}
    style={{ display: "flex", alignItems: "center", gap: 14, padding: "15px 0", borderBottom: "1px solid "+L.border, background: "none", borderTop: "none", borderLeft: "none", borderRight: "none", cursor: "pointer", width: "100%", textAlign: "left", fontFamily: "inherit" }}
    whileHover={{ x: 4 } as any} transition={{ duration: 0.15 }}>
    <div style={{ width: 38, height: 38, flexShrink: 0, background: "rgba(0,0,0,0.02)", border: "1px solid "+L.border, display: "flex", alignItems: "center", justifyContent: "center", color: danger ? "#b91c1c" : L.muted }}>{icon}</div>
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 14, color: danger ? "#b91c1c" : L.text, fontWeight: 500 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: L.textSoft, marginTop: 1 }}>{sub}</div>}
    </div>
    {!danger && <ChevronRight size={16} color={L.muted}/>}
  </motion.button>
);

const LMCard: React.FC<{ label: string; value: string; sub: string; up: boolean | null; icon: React.ReactNode }> = ({ label, value, sub, up, icon }) => (
  <div style={{ background: L.surface, border: "1px solid "+L.border, padding: "22px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <span style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.15em" }}>{label.toUpperCase()}</span>
      <span style={{ color: L.muted }}>{icon}</span>
    </div>
    <div style={{ fontSize: 28, fontWeight: 300, color: L.text, letterSpacing: "-0.02em", fontFamily: "'Cormorant Garamond',serif" }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: up === true ? L.text : L.muted }}>{sub}</div>}
  </div>
);

const LDCell: React.FC<{ l: string; v: string }> = ({ l, v }) => (
  <div style={{ padding: "13px 0", borderBottom: "1px solid "+L.border }}>
    <div style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.12em", marginBottom: 4 }}>{l.toUpperCase()}</div>
    <div style={{ fontSize: 13, color: L.text, fontWeight: 500 }}>{v}</div>
  </div>
);

const LRCard: React.FC<{ u: string; copied: boolean; onCopy: () => void }> = ({ u, copied, onCopy }) => (
  <motion.div style={{ background: L.surface, border: "1px solid "+L.border, padding: 20, marginBottom: 16, marginTop: 12 }} whileHover={{ borderColor: L.text } as any}>
    <div style={{ fontSize: 10, color: L.textSoft, letterSpacing: "0.15em", marginBottom: 10 }}>REFER &amp; EARN</div>
    <div style={{ fontSize: 18, fontWeight: 400, color: L.text, marginBottom: 6, fontFamily: "'Cormorant Garamond',serif" }}>Invite friends, earn {EUR}10 each</div>
    <p style={{ fontSize: 12, color: L.textSoft, marginBottom: 14, lineHeight: 1.6 }}>Both you and your friend receive {EUR}10 on their first transaction.</p>
    <div style={{ display: "flex", gap: 8 }}>
      <input readOnly value={"cosmoola.com/ref/" + u}
        style={{ flex: 1, background: L.surface2, border: "1px solid "+L.border, padding: "10px 12px", color: L.textSoft, fontSize: 11, outline: "none", fontFamily: "monospace" }}/>
      <motion.button onClick={onCopy}
        style={{ background: copied ? L.surface2 : L.text, color: copied ? L.text : L.bg, border: "1px solid "+L.text, padding: "10px 18px", cursor: "pointer", fontWeight: 600, fontSize: 11, display: "flex", alignItems: "center", gap: 6, fontFamily: "inherit", letterSpacing: "0.1em" }}
        whileTap={{ scale: 0.95 } as any}>
        {copied ? <><CheckCircle size={12}/> COPIED</> : <><Copy size={12}/> COPY</>}
      </motion.button>
    </div>
  </motion.div>
);

/* ─── CSS ─── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  body{background:#000}button{font-family:inherit}input{font-family:inherit}textarea{font-family:inherit}
  .mob-only{display:block}.desk-only{display:none}
  .brand-center{font-size:15px}
  .desk-contact{display:flex}
  .menu-label{display:inline}
  @media(min-width:900px){
    .mob-only{display:none!important}.desk-only{display:block!important}
    .brand-center{font-size:20px!important}
  }
  @media(max-width:600px){
    .brand-center{font-size:13px!important}
    .desk-contact{display:none!important}
    .menu-label{display:none!important}
  }
  @media(max-width:1100px){.desk-metrics{grid-template-columns:repeat(2,1fr)!important}.desk-grid{grid-template-columns:1fr!important}}
  ::-webkit-scrollbar{width:0;height:0}
`;